/**
 * V2VariableCrudPanel
 *
 * Reusable CRUD panel for FunnelVariable management.
 * Props-driven (no context dependency) — receives callbacks for maximum reusability.
 *
 * Usage patterns:
 * - WebPlacementDialog: uses V2VariableCrudPanelConnected (inside VariableEditorProvider)
 * - Onboarding editor: uses V2VariableCrudPanelConnected (already has VariableEditorProvider)
 * - Custom context: uses V2VariableCrudPanel directly with own callbacks
 *
 * @module v2-variable-utils
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  Radio,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import type { FunnelVariable, VariableType } from '@segmently/schema-models/v2';
import type { CreateVariableParams } from '../types/variable-utils.types';
import { V2_VARIABLES } from '@test-kit/selectors/variable-privacy';
import { AnalyticsPrivacyDecisionField } from './AnalyticsPrivacyDecisionField';
import { V2VariableEditDialog } from './V2VariableEditDialog';

// ============================================================================
// TYPES
// ============================================================================

export interface V2VariableCrudPanelProps {
  /** All current variables keyed by ID */
  variables: Record<string, FunnelVariable>;
  /** Loading state */
  loading: boolean;
  /** Called when a variable is created */
  onCreateVariable: (params: CreateVariableParams) => Promise<FunnelVariable>;
  /** Called when a variable is updated */
  onUpdateVariable: (variable: FunnelVariable) => Promise<void>;
  /** Called when a variable is deleted */
  onDeleteVariable: (variableId: string) => Promise<void>;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Default scope for newly created variables ('screen' for screen editor, 'global' for placements) */
  defaultScope?: 'screen' | 'global';
  /** Available project locales for string default value dropdown */
  locales?: string[];
}

/** Variable types with display labels */
const VARIABLE_TYPE_OPTIONS: Array<{ value: VariableType; label: string }> = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'enum', label: 'Enum (single)' },
  { value: 'enum[]', label: 'Enum (multi)' },
];

interface NewVariableState {
  name: string;
  type: VariableType;
  description: string;
  excludeFromAnalytics?: boolean;
  options: Array<{ label: string; isDefault?: boolean }>;
}

const initialNewVariable: NewVariableState = {
  name: '',
  type: 'string',
  description: '',
  excludeFromAnalytics: undefined,
  options: [],
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * V2VariableCrudPanel
 *
 * Provides table-based CRUD interface for V2 FunnelVariable management.
 * Supports all variable types (string, number, boolean, date, enum, enum[]).
 */
export const V2VariableCrudPanel: React.FC<V2VariableCrudPanelProps> = ({
  variables,
  loading,
  onCreateVariable,
  onUpdateVariable,
  onDeleteVariable,
  disabled = false,
  defaultScope,
  locales,
}) => {
  const theme = useTheme();
  const [newVar, setNewVar] = useState<NewVariableState>(initialNewVariable);
  const [isAdding, setIsAdding] = useState(false);
  const [editDialogVariable, setEditDialogVariable] = useState<FunnelVariable | null>(null);
  const [saving, setSaving] = useState(false);
  const [privacyMenu, setPrivacyMenu] = useState<{
    anchor: HTMLElement;
    variable: FunnelVariable;
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const variablesList = useMemo(
    () => Object.values(variables).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [variables]
  );

  // Selection is derived against the CURRENT list so externally removed
  // variables never linger in bulk targets.
  const selectedVariables = useMemo(
    () => variablesList.filter((v) => selectedIds.has(v.id)),
    [variablesList, selectedIds]
  );
  const allSelected = variablesList.length > 0 && selectedVariables.length === variablesList.length;
  const someSelected = selectedVariables.length > 0 && !allSelected;

  const isEnumType = (type: VariableType) => type === 'enum' || type === 'enum[]';

  // --- Add handlers ---

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewVar(initialNewVariable);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewVar(initialNewVariable);
  }, []);

  const handleTypeChange = useCallback((type: VariableType) => {
    setNewVar((prev) => ({
      ...prev,
      type,
      // Initialize 2 default options for enum types
      options: (type === 'enum' || type === 'enum[]')
        ? [{ label: 'Option 1' }, { label: 'Option 2' }]
        : [],
    }));
  }, []);

  const handleAddOption = useCallback(() => {
    setNewVar((prev) => ({
      ...prev,
      options: [...prev.options, { label: `Option ${prev.options.length + 1}` }],
    }));
  }, []);

  const handleRemoveOption = useCallback((index: number) => {
    setNewVar((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  }, []);

  const handleOptionLabelChange = useCallback((index: number, label: string) => {
    setNewVar((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? { ...o, label } : o)),
    }));
  }, []);

  const handleOptionDefaultToggle = useCallback((index: number) => {
    setNewVar((prev) => {
      const isEnum = prev.type === 'enum';
      return {
        ...prev,
        options: prev.options.map((o, i) => {
          if (isEnum) {
            // Radio behavior: only one can be default
            return { ...o, isDefault: i === index ? !o.isDefault : false };
          }
          // Checkbox behavior: toggle individually
          return i === index ? { ...o, isDefault: !o.isDefault } : o;
        }),
      };
    });
  }, []);

  const handleSaveNew = useCallback(async () => {
    if (!newVar.name.trim() || typeof newVar.excludeFromAnalytics !== 'boolean') return;

    setSaving(true);
    try {
      const params: CreateVariableParams = {
        name: newVar.name.trim(),
        type: newVar.type,
        excludeFromAnalytics: newVar.excludeFromAnalytics,
        ...(defaultScope && { scope: defaultScope }),
        ...(newVar.description.trim() && { description: newVar.description.trim() }),
      };

      // Add options for enum types
      if (isEnumType(newVar.type) && newVar.options.length > 0) {
        params.options = newVar.options
          .filter((o) => o.label.trim())
          .map((o) => ({
            id: crypto.randomUUID(),
            label: o.label.trim(),
            ...(o.isDefault && { isDefault: true }),
          }));
      }

      await onCreateVariable(params);
      setIsAdding(false);
      setNewVar(initialNewVariable);
    } catch (error) {
      console.error('[V2VariableCrudPanel] Create failed:', error);
    } finally {
      setSaving(false);
    }
  }, [newVar, onCreateVariable, defaultScope]);

  // --- Edit handlers (dialog-based) ---

  const handleEditStart = useCallback((variable: FunnelVariable) => {
    setEditDialogVariable(variable);
  }, []);

  const handleEditDialogSave = useCallback(async (updated: FunnelVariable) => {
    setSaving(true);
    try {
      await onUpdateVariable({ ...updated, updatedAt: new Date() });
      setEditDialogVariable(null);
    } catch (error) {
      console.error('[V2VariableCrudPanel] Update failed:', error);
    } finally {
      setSaving(false);
    }
  }, [onUpdateVariable]);

  // --- Analytics privacy decision (inline chip menu) ---

  const handlePrivacyDecision = useCallback(async (excludeFromAnalytics: boolean) => {
    if (!privacyMenu) return;
    const { variable } = privacyMenu;
    setPrivacyMenu(null);
    if (variable.excludeFromAnalytics === excludeFromAnalytics) return;

    setSaving(true);
    try {
      await onUpdateVariable({ ...variable, excludeFromAnalytics, updatedAt: new Date() });
    } catch (error) {
      console.error('[V2VariableCrudPanel] Privacy decision update failed:', error);
    } finally {
      setSaving(false);
    }
  }, [privacyMenu, onUpdateVariable]);

  // --- Bulk selection handlers (variables-bulk-actions) ---

  const handleToggleSelect = useCallback((variableId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(variableId)) next.delete(variableId); else next.add(variableId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === variablesList.length ? new Set() : new Set(variablesList.map((v) => v.id)));
  }, [variablesList]);

  /**
   * Sequential per-variable updates mirroring the single-row no-op skip:
   * rows already holding the target value are not rewritten (no updatedAt
   * churn). Failures are continue-and-report — successful rows keep their NEW
   * value, failed rows keep the OLD one, busy always releases, selection stays.
   */
  const handleBulkPrivacy = useCallback(async (excludeFromAnalytics: boolean) => {
    setBulkMenuAnchor(null);
    const targets = selectedVariables.filter((v) => v.excludeFromAnalytics !== excludeFromAnalytics);
    if (targets.length === 0) return;

    setSaving(true);
    try {
      for (const variable of targets) {
        try {
          await onUpdateVariable({ ...variable, excludeFromAnalytics, updatedAt: new Date() });
        } catch (error) {
          console.error('[V2VariableCrudPanel] Bulk privacy update failed:', variable.id, error);
        }
      }
    } finally {
      setSaving(false);
    }
  }, [selectedVariables, onUpdateVariable]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    setSaving(true);
    const failed = new Set<string>();
    try {
      for (const variable of selectedVariables) {
        try {
          await onDeleteVariable(variable.id);
        } catch (error) {
          failed.add(variable.id);
          console.error('[V2VariableCrudPanel] Bulk delete failed:', variable.id, error);
        }
      }
    } finally {
      setSaving(false);
      setConfirmBulkDelete(false);
      setSelectedIds(failed);
    }
  }, [selectedVariables, onDeleteVariable]);

  // --- Delete handler ---

  const handleDelete = useCallback(async (variableId: string) => {
    setSaving(true);
    try {
      await onDeleteVariable(variableId);
    } catch (error) {
      console.error('[V2VariableCrudPanel] Delete failed:', error);
    } finally {
      setSaving(false);
    }
  }, [onDeleteVariable]);

  // --- Type chip color ---

  const getTypeColor = (type: VariableType): 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' => {
    switch (type) {
      case 'string': return 'primary';
      case 'number': return 'info';
      case 'boolean': return 'success';
      case 'date': return 'warning';
      case 'enum': return 'secondary';
      case 'enum[]': return 'error';
      default: return 'primary';
    }
  };

  const getOptionsCount = (v: FunnelVariable): string | null => {
    if (!isEnumType(v.type) || !v.options?.length) return null;
    return `${v.options.length} opts`;
  };

  // --- Loading state ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Loading variables...
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid={V2_VARIABLES.panel}>
      {/* Header (selection controls are inline — layout does not shift at 0 selected) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TuneIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
        <Typography variant="body2" color="text.secondary">
          V2 funnel variables — available for conditions and @variable text substitution
        </Typography>
        {selectedVariables.length > 0 && (
          <>
            <Chip
              data-testid={V2_VARIABLES.selectionCount}
              label={selectedVariables.length}
              size="small"
              color="primary"
              sx={{ fontWeight: 'medium' }}
            />
            <Button
              data-testid={V2_VARIABLES.bulkAnalyticsMenu}
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
              disabled={disabled || saving}
              sx={{ textTransform: 'none' }}
            >
              Analytics
            </Button>
            <Tooltip title="Delete selected">
              <span>
                <IconButton
                  data-testid={V2_VARIABLES.bulkDelete}
                  size="small"
                  color="error"
                  onClick={() => setConfirmBulkDelete(true)}
                  disabled={disabled || saving}
                  aria-label="Delete selected"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Clear selection">
              <IconButton
                data-testid={V2_VARIABLES.clearSelection}
                size="small"
                onClick={() => setSelectedIds(new Set())}
                aria-label="Clear selection"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid={V2_VARIABLES.addButton}
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled || saving}
          >
            Add Variable
          </Button>
        )}
      </Box>

      {/* Variables Table */}
      {variablesList.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          {/* minWidth lets the container scroll instead of clipping the Analytics column */}
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    data-testid={V2_VARIABLES.selectAll}
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    disabled={disabled || saving}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: '28%' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%' }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Analytics</TableCell>
                <TableCell align="right" sx={{ width: '14%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variablesList.map((v) => (
                <TableRow key={v.id} hover selected={selectedIds.has(v.id)} data-testid={V2_VARIABLES.row(v.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      data-testid={V2_VARIABLES.rowCheckbox(v.id)}
                      size="small"
                      checked={selectedIds.has(v.id)}
                      onChange={() => handleToggleSelect(v.id)}
                      disabled={disabled || saving}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {v.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={v.id}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        maxWidth: 140,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={v.type}
                      size="small"
                      color={getTypeColor(v.type)}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    {getOptionsCount(v) && (
                      <Typography variant="caption" color="text.secondary">
                        {getOptionsCount(v)}
                      </Typography>
                    )}
                    {(v as unknown as Record<string, unknown>).scope === 'global' ? (
                      <Chip
                        label="global"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', ml: 0.5 }}
                      />
                    ) : v.boundScreenIds?.length ? (
                      <Tooltip title={`Bound to ${v.boundScreenIds.length} screen(s)`}>
                        <Chip
                          label={`${v.boundScreenIds.length} bound`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', ml: 0.5 }}
                        />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Analytics collection decision — click to change">
                      <Chip
                        data-testid={V2_VARIABLES.status(v.id)}
                        label={v.excludeFromAnalytics === true
                          ? 'Not collected'
                          : v.excludeFromAnalytics === false
                            ? 'Collected'
                            : 'Review required'}
                        size="small"
                        color={v.excludeFromAnalytics === true
                          ? 'default'
                          : v.excludeFromAnalytics === false
                            ? 'success'
                            : 'warning'}
                        variant={v.excludeFromAnalytics === undefined ? 'filled' : 'outlined'}
                        onClick={(event) => setPrivacyMenu({ anchor: event.currentTarget, variable: v })}
                        onDelete={(event: React.MouseEvent<HTMLElement>) => setPrivacyMenu({ anchor: event.currentTarget, variable: v })}
                        deleteIcon={<ArrowDropDownIcon />}
                        disabled={disabled || saving}
                        sx={{ fontSize: '0.65rem' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      data-testid={V2_VARIABLES.editButton(v.id)}
                      size="small"
                      onClick={() => handleEditStart(v)}
                      disabled={disabled || saving}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      data-testid={V2_VARIABLES.deleteButton(v.id)}
                      size="small"
                      onClick={() => handleDelete(v.id)}
                      disabled={disabled || saving}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Variable Form */}
      {isAdding && (
        <Box
          data-testid={V2_VARIABLES.createForm}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <TextField
            data-testid={V2_VARIABLES.createNameInput}
            label="Variable Name"
            value={newVar.name}
            onChange={(e) => setNewVar((prev) => ({ ...prev, name: e.target.value }))}
            size="small"
            fullWidth
            required
            placeholder="e.g. Traffic Source"
            helperText={newVar.name.trim() ? `ID: ${newVar.name.toLowerCase().trim().replace(/[^a-z0-9\s_-]/g, '').replace(/[\s-]+/g, '_')}` : 'Human-readable name'}
            autoFocus
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={newVar.type}
              onChange={(e) => handleTypeChange(e.target.value as VariableType)}
              label="Type"
            >
              {VARIABLE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            data-testid={V2_VARIABLES.createDescriptionInput}
            label="Description (optional)"
            value={newVar.description}
            onChange={(e) => setNewVar((prev) => ({ ...prev, description: e.target.value }))}
            size="small"
            fullWidth
            placeholder="For AI context and documentation"
          />

          <AnalyticsPrivacyDecisionField
            value={newVar.excludeFromAnalytics}
            onChange={(value) => setNewVar((previous) => ({
              ...previous,
              excludeFromAnalytics: value,
            }))}
            testIdPrefix={V2_VARIABLES.createDecisionPrefix}
            disabled={saving}
          />

          {/* Enum Options Editor */}
          {isEnumType(newVar.type) && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: alpha(theme.palette.secondary.main, 0.04),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
                  Options
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ width: 56, textAlign: 'center' }}>
                  Default
                </Typography>
                <Box sx={{ width: 32 }} />
              </Box>
              {newVar.options.map((opt, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    value={opt.label}
                    onChange={(e) => handleOptionLabelChange(i, e.target.value)}
                    size="small"
                    fullWidth
                    placeholder={`Option ${i + 1}`}
                  />
                  <Tooltip title="Content fallback when no user selection">
                    <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                      {newVar.type === 'enum' ? (
                        <Radio
                          size="small"
                          checked={!!opt.isDefault}
                          onChange={() => handleOptionDefaultToggle(i)}
                          sx={{ p: 0.5 }}
                        />
                      ) : (
                        <Checkbox
                          size="small"
                          checked={!!opt.isDefault}
                          onChange={() => handleOptionDefaultToggle(i)}
                          sx={{ p: 0.5 }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveOption(i)}
                    disabled={newVar.options.length <= 1}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddOption}>
                Add Option
              </Button>
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleCancelAdd} disabled={saving}>
              Cancel
            </Button>
            <Button
              data-testid={V2_VARIABLES.createButton}
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newVar.name.trim()
                || typeof newVar.excludeFromAnalytics !== 'boolean'
                || saving}
            >
              {saving ? <CircularProgress size={16} /> : 'Add'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Empty State */}
      {variablesList.length === 0 && !isAdding && (
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No variables defined yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Create variables to bind URL params or server data for conditions and @variable text.
          </Typography>
        </Box>
      )}

      {/* Bulk analytics decision menu */}
      <Menu
        anchorEl={bulkMenuAnchor}
        open={Boolean(bulkMenuAnchor)}
        onClose={() => setBulkMenuAnchor(null)}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
          Set for {selectedVariables.length} selected
        </Typography>
        <MenuItem
          data-testid={V2_VARIABLES.bulkAnalyticsCollect}
          onClick={() => handleBulkPrivacy(false)}
        >
          <ListItemText primary="Collect in analytics" />
        </MenuItem>
        <MenuItem
          data-testid={V2_VARIABLES.bulkAnalyticsExclude}
          onClick={() => handleBulkPrivacy(true)}
        >
          <ListItemText primary="Don't collect" />
        </MenuItem>
      </Menu>

      {/* Bulk delete confirm — norm: default header X is the single dismiss,
          error primary NAMES the action, no footer Cancel */}
      <SDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        maxWidth="sm"
        data-testid={V2_VARIABLES.bulkDeleteConfirmDialog}
        title={`Delete ${selectedVariables.length} variable${selectedVariables.length === 1 ? '' : 's'}?`}
        primaryAction={{
          label: `Delete ${selectedVariables.length} variable${selectedVariables.length === 1 ? '' : 's'}`,
          onClick: handleBulkDeleteConfirm,
          color: 'error',
          loading: saving,
          'data-testid': V2_VARIABLES.bulkDeleteConfirmButton,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {selectedVariables.map((v) => v.name).join(', ')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Screens bound to these variables lose their bindings. This action cannot be undone.
          </Typography>
        </Box>
      </SDialog>

      {/* Analytics privacy decision menu (opened from the status chip) */}
      <Menu
        anchorEl={privacyMenu?.anchor ?? null}
        open={!!privacyMenu}
        onClose={() => setPrivacyMenu(null)}
      >
        <MenuItem
          data-testid={V2_VARIABLES.privacyMenuCollect}
          onClick={() => handlePrivacyDecision(false)}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            {privacyMenu?.variable.excludeFromAnalytics === false && <CheckIcon fontSize="small" color="primary" />}
          </ListItemIcon>
          <ListItemText primary="Collect in analytics" />
        </MenuItem>
        <MenuItem
          data-testid={V2_VARIABLES.privacyMenuExclude}
          onClick={() => handlePrivacyDecision(true)}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            {privacyMenu?.variable.excludeFromAnalytics === true && <CheckIcon fontSize="small" color="primary" />}
          </ListItemIcon>
          <ListItemText primary="Don't collect" />
        </MenuItem>
      </Menu>

      {/* Edit Variable Dialog */}
      {editDialogVariable && (
        <V2VariableEditDialog
          open={!!editDialogVariable}
          variable={editDialogVariable}
          onSave={handleEditDialogSave}
          onClose={() => setEditDialogVariable(null)}
          disabled={disabled}
          locales={locales}
        />
      )}
    </Box>
  );
};

// ============================================================================
// CONNECTED WRAPPER (uses VariableEditorContext)
// ============================================================================

/**
 * V2VariableCrudPanelConnected
 *
 * Thin wrapper that reads from useVariableEditor() context and passes to V2VariableCrudPanel.
 * Requires VariableEditorProvider ancestor.
 */
export const V2VariableCrudPanelConnected: React.FC<{ disabled?: boolean; defaultScope?: 'screen' | 'global'; locales?: string[] }> = ({ disabled, defaultScope, locales }) => {
  // Lazy import to avoid circular deps — VariableEditorContext is in reactFlow module
  const { useVariableEditor } = require('@modules/reactFlow/contexts/VariableEditorContext');
  const { variables, loading, createVariable, updateVariable, deleteVariable } = useVariableEditor();

  return (
    <V2VariableCrudPanel
      variables={variables}
      loading={loading}
      onCreateVariable={createVariable}
      onUpdateVariable={updateVariable}
      onDeleteVariable={deleteVariable}
      disabled={disabled}
      defaultScope={defaultScope}
      locales={locales}
    />
  );
};

export default V2VariableCrudPanel;
